import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/SamsoftixLogo.jpg';
import './Navbar.css';

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [tapCount, setTapCount] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdowns, setDropdowns] = useState({
    blogs: false,
    services: false,
    products: false,
  });

  const navbarRef = useRef(null);

  // ---------------------------------------
  // Secret admin login - 5 quick taps
  // ---------------------------------------
  useEffect(() => {
    if (tapCount >= 5) {
      navigate('/alogin');
      setTapCount(0);
    }

    const timeout = setTimeout(() => {
      setTapCount(0);
    }, 2500);

    return () => clearTimeout(timeout);
  }, [tapCount, navigate]);

  // ---------------------------------------
  // Desktop 5-click admin shortcut
  // ---------------------------------------
  const handleLogoClick = (e) => {
    if (e.detail === 5) {
      navigate('/alogin');
    }
  };

  // ---------------------------------------
  // Mobile tap admin shortcut
  // ---------------------------------------
  const handleLogoTouch = () => {
    const now = Date.now();

    if (now - lastTapTime < 600) {
      setTapCount((prev) => prev + 1);
    } else {
      setTapCount(1);
    }

    setLastTapTime(now);
  };

  // ---------------------------------------
  // Close all menus
  // ---------------------------------------
  const closeAllMenus = () => {
    setMenuOpen(false);
    setDropdowns({
      blogs: false,
      services: false,
    });
  };

  // ---------------------------------------
  // Mobile menu
  // ---------------------------------------
  const toggleMenu = () => {
    setMenuOpen((prev) => !prev);

    setDropdowns({
      blogs: false,
      services: false,
      products: false,
    });
  };

  // ---------------------------------------
  // Dropdown handling
  // ---------------------------------------
  const toggleDropdown = (name) => {
    setDropdowns((prev) => ({
      blogs: name === 'blogs' ? !prev.blogs : false,
      services: name === 'services' ? !prev.services : false,
      products: name === 'products' ? !prev.products : false,
    }));
  };

  // ---------------------------------------
  // Close dropdowns when route changes
  // ---------------------------------------
  useEffect(() => {
    closeAllMenus();
  }, [location.pathname]);

  // ---------------------------------------
  // Close menu when clicking outside
  // ---------------------------------------
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        navbarRef.current &&
        !navbarRef.current.contains(event.target)
      ) {
        setDropdowns({
          blogs: false,
          services: false,
          products: false,
        });
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  // ---------------------------------------
  // Active route helper
  // ---------------------------------------
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav
      ref={navbarRef}
      className={`navbar ${menuOpen ? 'navbar-menu-open' : ''}`}
    >
      <div className="navbar-inner">

        {/* ================================
            BRAND
        ================================= */}
        <div
          className="logo-container"
          onClick={handleLogoClick}
          onTouchStart={handleLogoTouch}
          title="SamSoftix - Smart Software Solutions"
          aria-label="SamSoftix Home"
        >
          <Link
            to="/"
            className="brand-link"
            onClick={closeAllMenus}
          >
            <div className="logo-wrapper">
              <img
                src={logo}
                alt="SamSoftix Software House Logo"
                className="logo"
              />
            </div>

            <div className="brand-text">
              <h1 className="company-name">
                <span className="sam">Sam</span>
                <span className="softix">Softix</span>
              </h1>

              <span className="brand-tagline">
                Software • Web • AI
              </span>
            </div>
          </Link>
        </div>

        {/* ================================
            MOBILE MENU BUTTON
        ================================= */}
        <button
          type="button"
          className={`menu-toggle ${menuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label={
            menuOpen
              ? 'Close navigation menu'
              : 'Open navigation menu'
          }
          aria-expanded={menuOpen}
        >
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </button>

        {/* ================================
            NAVIGATION
        ================================= */}
        <div className={`nav-wrapper ${menuOpen ? 'active' : ''}`}>
          <ul className="nav-links">

            {/* HOME */}
            <li>
              <Link
                to="/"
                className={isActive('/') ? 'active-link' : ''}
                onClick={closeAllMenus}
              >
                <span>Home</span>
              </Link>
            </li>

            {/* ABOUT */}
            <li>
              <Link
                to="/about"
                className={isActive('/about') ? 'active-link' : ''}
                onClick={closeAllMenus}
              >
                <span>About</span>
              </Link>
            </li>

            {/* ================================
                BLOGS DROPDOWN
            ================================= */}
            <li
              className={`dropdown ${
                dropdowns.blogs ? 'dropdown-open' : ''
              }`}
            >
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => toggleDropdown('blogs')}
                aria-expanded={dropdowns.blogs}
              >
                <span>Blogs</span>

                <span
                  className={`arrow ${
                    dropdowns.blogs ? 'rotate' : ''
                  }`}
                >
                  ›
                </span>
              </button>

              <ul
                className={`dropdown-menu ${
                  dropdowns.blogs ? 'active' : ''
                }`}
              >
                <li>
                  <Link
                    to="/write_blog_requirements"
                    onClick={closeAllMenus}
                  >
                    Write Effective Project Requirements
                  </Link>
                </li>

                <li>
                  <Link
                    to="/Create_React_Js_App"
                    onClick={closeAllMenus}
                  >
                    Build a Responsive Website with React.js
                  </Link>
                </li>

                <li>
                  <Link
                    to="/React_Advanced_Tips"
                    onClick={closeAllMenus}
                  >
                    10 Advanced React.js Tips
                  </Link>
                </li>

                <li>
                  <Link
                    to="/GetHosting"
                    onClick={closeAllMenus}
                  >
                    Hosting Guide
                  </Link>
                </li>

                <li>
                  <Link
                    to="/HostingerHosting"
                    onClick={closeAllMenus}
                  >
                    Hostinger Hosting
                  </Link>
                </li>

                <li>
                  <Link
                    to="/React_Best_Practices"
                    onClick={closeAllMenus}
                  >
                    Modern Website Development Practices
                  </Link>
                </li>

                <li>
                  <Link
                    to="/StartUp_Website_2026"
                    onClick={closeAllMenus}
                  >
                    Why Startups Need a Professional Website
                  </Link>
                </li>

                <li>
                  <Link
                    to="/AI_Startup_blog"
                    onClick={closeAllMenus}
                  >
                    AI & Automation for Startups
                  </Link>
                </li>

                <li>
                  <Link
                    to="/UI_Design_Principles"
                    onClick={closeAllMenus}
                  >
                    Essential UI/UX Design Principles
                  </Link>
                </li>
              </ul>
            </li>

            {/* ================================
                SERVICES DROPDOWN
            ================================= */}
            <li
              className={`dropdown ${
                dropdowns.services ? 'dropdown-open' : ''
              }`}
            >
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => toggleDropdown('services')}
                aria-expanded={dropdowns.services}
              >
                <span>Services</span>

                <span
                  className={`arrow ${
                    dropdowns.services ? 'rotate' : ''
                  }`}
                >
                  ›
                </span>
              </button>

              <ul
                className={`dropdown-menu services-dropdown ${
                  dropdowns.services ? 'active' : ''
                }`}
              >
                <li>
                  <Link
                    to="/all_services"
                    onClick={closeAllMenus}
                  >
                    All Services
                  </Link>
                </li>

                <li>
                  <Link
                    to="/website_Service_Samsoftix"
                    onClick={closeAllMenus}
                  >
                    Web Development
                  </Link>
                </li>

                <li>
                  <Link
                    to="/GetHostingDomain"
                    onClick={closeAllMenus}
                  >
                    Hosting & Domain
                  </Link>
                </li>

                <li>
                  <Link
                    to="/Mobile_App_Service_SamSoftix"
                    onClick={closeAllMenus}
                  >
                    Mobile App Development
                  </Link>
                </li>

                <li>
                  <Link
                    to="/AI_Solutions_SamSoftix"
                    onClick={closeAllMenus}
                  >
                    AI Solutions
                  </Link>
                </li>

                <li>
                  <Link
                    to="/Dubai_Website_Service_SamSoftix"
                    onClick={closeAllMenus}
                  >
                    Dubai Website Services
                  </Link>
                </li>
              </ul>
            </li>

            {/* ================================
                Products DROPDOWN
            ================================= */}
            <li
              className={`dropdown ${
                dropdowns.products ? 'dropdown-open' : ''
              }`}
            >
              <button
                type="button"
                className="dropdown-trigger"
                onClick={() => toggleDropdown('products')}
                aria-expanded={dropdowns.products}
              >
                <span>Products</span>

                <span
                  className={`arrow ${
                    dropdowns.products ? 'rotate' : ''
                  }`}
                >
                  ›
                </span>
              </button>

              <ul
                className={`dropdown-menu services-dropdown ${
                  dropdowns.products ? 'active' : ''
                }`}
              >
                <li>
                  <Link
                    to="/UdharManagerApp"
                    onClick={closeAllMenus}
                  >
                    Udhar Manager App
                  </Link>
                </li>
              </ul>
            </li>

            {/* CONTACT */}
            <li>
              <Link
                to="/contact"
                className={
                  isActive('/contact') ? 'active-link' : ''
                }
                onClick={closeAllMenus}
              >
                <span>Contact</span>
              </Link>
            </li>

            {/* OFFER CTA */}
            <li className="nav-cta-item">
              <Link
                to="/offers"
                className="nav-offer-btn"
                onClick={closeAllMenus}
              >
                <span className="offer-dot"></span>
                Offers
              </Link>
            </li>

          </ul>
        </div>

      </div>

      {/* Decorative bottom glow */}
      <div className="navbar-glow"></div>
    </nav>
  );
}

export default Navbar;